import { Alert, Box, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Risk, TenantState } from 'types/engagement.types';

/** Small building blocks shared by the Engagement Center tabs and the customer screens. */

export const SectionCard: React.FC<{ title?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Paper variant={'outlined'} sx={{ p: 2.5, borderRadius: 2, borderColor: 'greys.lighter' }}>
    {title || action ? (
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 1.5 }} gap={2}>
        {title ? <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography> : <span />}
        {action}
      </Stack>
    ) : null}
    {children}
  </Paper>
);

export const KpiTile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'default' | 'warn' }> = ({
  label,
  value,
  sub,
  tone = 'default',
}) => (
  <Paper
    variant={'outlined'}
    data-testid={'kpi-tile'}
    sx={{ p: 2, borderRadius: 2, minWidth: 160, flex: '1 1 160px', borderColor: tone === 'warn' ? 'warning.main' : 'greys.lighter' }}
  >
    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{label}</Typography>
    <Box sx={{ fontSize: 26, fontWeight: 600, lineHeight: 1.2 }}>{value}</Box>
    {sub ? <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{sub}</Typography> : null}
  </Paper>
);

export const KpiRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
    {children}
  </Stack>
);

interface QueryStateProps {
  isLoading: boolean;
  error: unknown;
  isEmpty?: boolean;
  emptyText?: string;
  children: React.ReactNode;
}

/**
 * Loading, failure and emptiness look different on purpose: "keine Daten" and
 * "Abfrage fehlgeschlagen" must never be confused (contract: Fehlschlag ≠ Leere).
 */
export const QueryState: React.FC<QueryStateProps> = ({ isLoading, error, isEmpty, emptyText, children }) => {
  const { t } = useTranslation();
  if (isLoading) return <LinearProgress sx={{ my: 2 }} />;
  if (error) {
    const err = error as { message?: string; status?: number };
    return (
      <Alert severity={'error'} data-testid={'query-error'}>
        {err?.status === 404 ? t('engErrorNotAvailable') : t('engErrorLoad')}
        {err?.message ? ` (${err.message})` : ''}
      </Alert>
    );
  }
  if (isEmpty) {
    return (
      <Typography data-testid={'query-empty'} sx={{ color: 'text.secondary', fontSize: 14, py: 2 }}>
        {emptyText ?? t('engEmpty')}
      </Typography>
    );
  }
  return <>{children}</>;
};

const STATE_COLORS: Record<TenantState, { bg: string; fg: string }> = {
  healthy: { bg: '#E7F6EC', fg: '#1B7F3B' },
  no_pilot: { bg: '#FFF4E0', fg: '#A15C00' },
  blocked: { bg: '#FDE8E8', fg: '#B42318' },
  churn_risk: { bg: '#EEEEF3', fg: '#4A4A5A' },
};

export const StatePill: React.FC<{ state: TenantState }> = ({ state }) => {
  const { t } = useTranslation();
  const c = STATE_COLORS[state] ?? STATE_COLORS.churn_risk;
  return <Chip size={'small'} label={t(`engState_${state}`)} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600 }} />;
};

const RISK_COLORS: Record<Risk, { bg: string; fg: string }> = {
  stable: { bg: '#E7F6EC', fg: '#1B7F3B' },
  watch: { bg: '#FFF4E0', fg: '#A15C00' },
  at_risk: { bg: '#FDE8E8', fg: '#B42318' },
};

export const RiskPill: React.FC<{ risk: Risk }> = ({ risk }) => {
  const { t } = useTranslation();
  const c = RISK_COLORS[risk] ?? RISK_COLORS.watch;
  return <Chip size={'small'} label={t(`engRisk_${risk}`)} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600 }} />;
};
